import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import request from '../utils/request';

function OrderDetailsScreen() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [orderData, setOrderData] = useState(null);
  
  const [entryInstructions, setEntryInstructions] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');

  // Delivery time options (8:00 AM - 4:00 PM, 30-min intervals)
  const deliveryTimeOptions = [
    '8:00 AM', '8:30 AM', '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM',
    '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM',
    '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM'
  ];

  useEffect(() => {
    if (!token) {
      setError('Authentication token is missing. Please use the link from your email.');
      setLoading(false);
      return;
    }

    fetchOrderDetails();
  }, [orderId, token]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await request.get(`/orders/${orderId}/entry-instructions?token=${token}`);
      
      if (response.data.success) {
        setOrderData(response.data);
        setEntryInstructions(response.data.order.entryInstructions || '');
        setDeliveryTime(response.data.order.hostSetDeliveryTime || '');
      } else {
        setError(response.data.message || 'Failed to load order details');
      }
    } catch (err) {
      console.error('Error fetching order details:', err);
      setError(
        err.response?.data?.message || 
        'Failed to load order details. The link may have expired or is invalid.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!entryInstructions.trim()) {
      setError('Entry instructions are required');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const response = await request.post(
        `/orders/${orderId}/entry-instructions?token=${token}`,
        {
          entryInstructions: entryInstructions.trim(),
          deliveryTime: deliveryTime || undefined,
        }
      );

      if (response.data.success) {
        setSuccess(true);
        // Redirect after 3 seconds
        setTimeout(() => {
          window.close(); // Close the window/tab if opened from email
        }, 3000);
      } else {
        setError(response.data.message || 'Failed to submit entry instructions');
      }
    } catch (err) {
      console.error('Error submitting entry instructions:', err);
      setError(
        err.response?.data?.message || 
        'Failed to submit entry instructions. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error && !orderData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <div className="text-red-600 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.close()}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <div className="text-green-600 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Success!</h2>
          <p className="text-gray-600 mb-4">
            Your entry instructions have been submitted successfully.
          </p>
          <p className="text-sm text-gray-500">
            This window will close automatically...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Entry Instructions</h1>
            <img 
              src="https://bellhopt-public.s3.us-west-1.amazonaws.com/BellhoptLogoMedRes.png" 
              alt="Bellhopt" 
              className="h-10"
            />
          </div>
          <p className="text-gray-600">
            Please provide entry instructions and preferred delivery time for this order.
          </p>
        </div>

        {/* Order Details Card */}
        {orderData && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Details</h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Order ID</p>
                <p className="text-base font-medium text-gray-900">#{orderData.order.uniqueId}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Guest Email</p>
                <p className="text-base font-medium text-gray-900">{orderData.order.guestEmail}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Delivery Date</p>
                <p className="text-base font-medium text-gray-900">
                  {formatDate(orderData.order.inStockDate)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Delivery Address</p>
                <p className="text-base font-medium text-gray-900">
                  {orderData.property.address.line1}
                  {orderData.property.address.line2 && (
                    <><br />{orderData.property.address.line2}</>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Entry Instructions */}
          <div className="mb-6">
            <label htmlFor="entryInstructions" className="block text-sm font-medium text-gray-700 mb-2">
              Entry Instructions <span className="text-red-500">*</span>
            </label>
            <textarea
              id="entryInstructions"
              value={entryInstructions}
              onChange={(e) => setEntryInstructions(e.target.value)}
              rows={6}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
              placeholder="e.g., Keypad code is 1234, park in spot #7, enter through side door..."
            />
            <p className="mt-1 text-sm text-gray-500">
              This will update both the order entry instructions and property description.
            </p>
          </div>

          {/* Delivery Time */}
          <div className="mb-6">
            <label htmlFor="deliveryTime" className="block text-sm font-medium text-gray-700 mb-2">
              Delivery Time (when house cleaned)
            </label>
            <select
              id="deliveryTime"
              value={deliveryTime}
              onChange={(e) => setDeliveryTime(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select a time</option>
              {deliveryTimeOptions.map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              Preferred time for delivery when the house is cleaned.
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting || !entryInstructions.trim()}
              className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default OrderDetailsScreen;

